import React, { useEffect, useState } from 'react';
import { Users, Package, ShoppingCart, Crown, AreaChart, ArrowUpRight, Clock3, CreditCard, Sparkles, MessageSquare, Lightbulb, Bell } from 'lucide-react';
import { AreaChart as ReAreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import StatCard from '../components/StatCard';
import Topbar from '../components/Topbar';
import { supabase } from '../lib/supabase';
import { useAdminAuth } from '../context/AdminAuth';

const money = (amount: number, currency = 'GBP') =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(amount);

const buildMonthlyData = (orders: any[]) => {
  const months = Array.from({ length: 8 }, (_, index) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (7 - index));
    return {
      key: `${date.getFullYear()}-${date.getMonth()}`,
      month: date.toLocaleString('en-GB', { month: 'short' }),
      orders: 0,
      revenue: 0,
    };
  });

  orders.forEach(order => {
    if (!order.created_at) return;
    const date = new Date(order.created_at);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const bucket = months.find(month => month.key === key);
    if (!bucket) return;
    bucket.orders += 1;
    bucket.revenue += Number(order.total_amount ?? 0);
  });

  return months.map(({ key, ...month }) => month);
};

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    premiumUsers: 0,
    suspendedUsers: 0,
    deletingUsers: 0,
    freeUsers: 0,
    totalProducts: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalOrders: 0,
    totalPosts: 0,
    activeTips: 0,
    unreadNotifications: 0,
    pushTokens: 0,
    totalRevenue: 0,
    currency: 'GBP',
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Was its own getUser() + profile query on every dashboard mount. The
  // identity is already resolved once at the root.
  const { identity } = useAdminAuth();
  const adminName = identity?.fullName?.split(' ')[0] ?? 'Admin';
  useEffect(() => {
    async function fetchStats() {
      // `select('id')` rather than `select('*')` on every count.
      //
      // With head:true PostgREST returns no rows either way, but it still
      // resolves the column list against the caller's grants — and migration
      // 0010 revokes column-level SELECT on the private half of `profiles`, so
      // `select('*')` there fails outright rather than returning a count.
      //
      // 'delivered' replaces 'completed': the app's status vocabulary is
      // pending / processing / shipped / delivered / cancelled, and this tile
      // was counting a status nothing in the system sets any more.
      const [
        { count: totalUsers },
        { count: premiumUsers },
        { count: suspendedUsers },
        { count: deletingUsers },
        { count: totalProducts },
        { count: pendingOrders },
        { count: completedOrders },
        { count: totalOrders },
        { count: totalPosts },
        { count: activeTips },
        { count: unreadNotifications },
        { count: pushTokens },
        { data: orders },
      ] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('is_premium', true),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).not('suspended_at', 'is', null),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).not('deactivated_at', 'is', null),
        supabase.from('products').select('id', { count: 'exact', head: true }),
        supabase.from('orders').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('orders').select('id', { count: 'exact', head: true }).eq('status', 'delivered'),
        supabase.from('orders').select('id', { count: 'exact', head: true }),
        supabase.from('posts').select('id', { count: 'exact', head: true }),
        supabase.from('hair_tips').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('notifications').select('id', { count: 'exact', head: true }).eq('is_read', false),
        supabase.from('push_tokens').select('id', { count: 'exact', head: true }),
        supabase.from('orders').select('id, created_at, status, total_amount, currency, delivery_address').order('created_at', { ascending: false }).limit(250),
      ]);

      const orderRows = orders ?? [];
      // Cancelled orders were being counted as revenue. A refunded or
      // cancelled order is money that did not arrive, so the headline number
      // on this page was reading high by exactly the amount you refunded.
      const totalRevenue = orderRows
        .filter(order => order.status !== 'cancelled')
        .reduce((sum, order) => sum + Number(order.total_amount ?? 0), 0);
      const primaryCurrency = orderRows.find(order => order.currency)?.currency ?? 'GBP';

      setStats({
        totalUsers: totalUsers ?? 0,
        premiumUsers: premiumUsers ?? 0,
        suspendedUsers: suspendedUsers ?? 0,
        deletingUsers: deletingUsers ?? 0,
        freeUsers: (totalUsers ?? 0) - (premiumUsers ?? 0),
        totalProducts: totalProducts ?? 0,
        pendingOrders: pendingOrders ?? 0,
        completedOrders: completedOrders ?? 0,
        totalOrders: totalOrders ?? 0,
        totalPosts: totalPosts ?? 0,
        activeTips: activeTips ?? 0,
        unreadNotifications: unreadNotifications ?? 0,
        pushTokens: pushTokens ?? 0,
        totalRevenue,
        currency: primaryCurrency,
      });
      setRecentOrders(orderRows.slice(0, 6));
      setMonthlyData(buildMonthlyData(orderRows));
      setLoading(false);
    }
    fetchStats();
  }, []);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: 'badge badge-warning',
      completed: 'badge badge-success',
      cancelled: 'badge badge-danger',
    };
    return map[status] ?? 'badge badge-primary';
  };

  return (
    <div>
      <Topbar title="Dashboard" />
      <div className="page-shell">

        {/* Welcome Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #6c5ffc 0%, #8b7ffd 50%, #a99cfe 100%)',
          borderRadius: 'var(--radius-xl)',
          padding: '30px 32px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#fff',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'var(--shadow-glow)',
          border: '1px solid rgba(255,255,255,0.18)',
        }}>
          <div style={{ position: 'absolute', top: -60, right: 220, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'absolute', bottom: -55, right: 80, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(255,255,255,0.12), transparent 38%)' }} />
          <div style={{ position: 'relative', maxWidth: 520 }}>
            <div className="metric-pill" style={{ marginBottom: 14 }}><Sparkles size={14} /> Live overview</div>
            <div style={{ fontSize: 30, fontWeight: 850, marginBottom: 8, letterSpacing: '-0.05em' }}>Welcome back, {adminName}</div>
            <div style={{ fontSize: 14, opacity: 0.86, lineHeight: 1.6 }}>A clearer snapshot of customers, commerce, and product activity across Zorax today.</div>
          </div>
          <div style={{ textAlign: 'right', position: 'relative' }}>
            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>Total Revenue</div>
            <div style={{ fontSize: 38, fontWeight: 850, letterSpacing: '-0.06em' }}>{money(stats.totalRevenue, stats.currency)}</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '7px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.16)', fontSize: 12, fontWeight: 800 }}>
              <ArrowUpRight size={14} /> {stats.totalOrders} live orders
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="dashboard-grid">
          {/*
            The `trend` values here were hardcoded — 4.2, 2.1, -1.3, 8.5 were
            typed in, not measured, so every one of these cards has been
            reporting an invented percentage since the day it was written.
            Removed rather than faked more convincingly; a number nobody
            computed is worse than no number, because you'd act on it.
          */}
          <StatCard title="Total Users"      value={stats.totalUsers}     icon={Users}        iconColor="#6c5ffc" iconBg="#ebe8ff" />
          <StatCard title="Premium Users"    value={stats.premiumUsers}   icon={Crown}        iconColor="#f7b731" iconBg="#fff3cd" />
          <StatCard title="Free Users"       value={stats.freeUsers}      icon={Users}        iconColor="#19b159" iconBg="#d4f5e2" />
          <StatCard title="Suspended"        value={stats.suspendedUsers} icon={Users}        iconColor="#e64b5d" iconBg="#fdecef" />
          <StatCard title="Pending Deletion" value={stats.deletingUsers}  icon={Users}        iconColor="#b4501a" iconBg="#fdf0e6" />
          <StatCard title="Total Products"   value={stats.totalProducts}  icon={Package}      iconColor="#4ec2f0" iconBg="#e0f5fd" />
          <StatCard title="Pending Orders"   value={stats.pendingOrders}  icon={ShoppingCart} iconColor="#f16d75" iconBg="#fde8e8" />
          <StatCard title="Delivered Orders" value={stats.completedOrders}icon={ShoppingCart} iconColor="#19b159" iconBg="#d4f5e2" />
          <StatCard title="Community Posts"  value={stats.totalPosts}     icon={MessageSquare} iconColor="#6c5ffc" iconBg="#ebe8ff" />
          <StatCard title="Active Hair Tips" value={stats.activeTips}     icon={Lightbulb}     iconColor="#f7b731" iconBg="#fff3cd" />
          <StatCard title="Unread Alerts"    value={stats.unreadNotifications} icon={Bell}    iconColor="#f16d75" iconBg="#fde8e8" />
        </div>

        {/* Charts + Recent Orders */}
        <div className="dashboard-panel-grid">
          {/* Revenue Chart */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>Monthly Revenue & Orders</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Last 8 months overview</div>
              </div>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AreaChart size={18} color="var(--primary)" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={270}>
              <ReAreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="revenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6c5ffc" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#6c5ffc" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="orders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f16d75" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#f16d75" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceef7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#8492a6' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#8492a6' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 14, border: '1px solid #e7e9f2', boxShadow: '0 14px 30px rgba(31,35,63,0.12)', fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="#6c5ffc" strokeWidth={2.5} fill="url(#revenue)" name="Revenue (£)" />
                <Area type="monotone" dataKey="orders"  stroke="#f16d75" strokeWidth={2.5} fill="url(#orders)"  name="Orders" />
              </ReAreaChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>Operations Pulse</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Fast signals that need admin attention</div>
            </div>
            {[
              { label: 'Pending orders', value: stats.pendingOrders, helper: 'Waiting for action', icon: Clock3, color: '#f16d75', bg: '#fde8e8' },
              { label: 'Community posts', value: stats.totalPosts, helper: 'Shared by users', icon: ShoppingCart, color: '#19b159', bg: '#d4f5e2' },
              { label: 'Push tokens', value: stats.pushTokens, helper: 'Reachable devices', icon: CreditCard, color: '#4ec2f0', bg: '#e0f5fd' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid var(--border)', borderRadius: 16, background: 'var(--surface-soft)' }}>
                <div style={{ width: 42, height: 42, borderRadius: 13, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <item.icon size={18} color={item.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{item.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{item.helper}</div>
                </div>
                <div style={{ fontSize: 22, fontWeight: 850, color: 'var(--text-primary)', letterSpacing: '-0.04em' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="card">
          <div className="page-header" style={{ marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Recent Orders</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Latest 6 orders</div>
            </div>
            <Link to="/orders" className="btn btn-sm" style={{ color: 'var(--primary)', background: 'var(--primary-light)', border: 'none' }}>View all <ArrowUpRight size={13} /></Link>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map(order => (
                  <tr key={order.id}>
                    <td style={{ fontWeight: 600, color: 'var(--primary)' }}>#{order.id.slice(0, 8).toUpperCase()}</td>
                    <td>{order.delivery_address?.full_name ?? '—'}</td>
                    <td style={{ fontWeight: 600 }}>{money(Number(order.total_amount ?? 0), order.currency ?? 'GBP')}</td>
                    <td>{new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td><span className={statusBadge(order.status)}>{order.status}</span></td>
                  </tr>
                ))}
                {recentOrders.length === 0 && !loading && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
